import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { fetchWithAuth } from '../utils/api';

export interface User {
  id: string;
  username: string;
  email: string;
  role: string;
}

interface AuthContextProps {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  login: (credentials: { identifier: string; password: string }) => Promise<void>;
  logout: () => void;
}

// Create context with default values
export const AuthContext = createContext<AuthContextProps | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [heartbeatInterval, setHeartbeatInterval] = useState<NodeJS.Timeout | null>(null);

  // Send a heartbeat to the server to update active status
  const sendHeartbeat = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      console.log('Sending user activity heartbeat...');
      // Try multiple endpoint formats to ensure one works in production
      const endpoints = [
        '/api/auth/heartbeat/',
        '/auth/heartbeat/',
        '/heartbeat/'
      ];
      
      // Try each endpoint until one works
      let success = false;
      for (const endpoint of endpoints) {
        try {
          await fetchWithAuth(endpoint, {
            method: 'POST'
          }, 0, true); // silent mode
          success = true;
          break;
        } catch (error) {
          console.log(`Heartbeat failed for ${endpoint}:`, error);
          // Continue trying other endpoints
        }
      }
      
      if (!success) {
        console.warn('All heartbeat endpoints failed');
      }
    } catch (error) {
      // Just log the error but don't show to user
      console.error('Heartbeat error:', error);
    }
  };

  // Set up heartbeat mechanism when user is authenticated
  useEffect(() => {
    if (currentUser) {
      // Send initial heartbeat
      sendHeartbeat();
      
      // Set up interval to send heartbeat every 5 minutes
      const interval = setInterval(sendHeartbeat, 5 * 60 * 1000);
      setHeartbeatInterval(interval);
      
      // Clean up interval on unmount or when user logs out
      return () => {
        if (interval) clearInterval(interval);
      };
    } else if (heartbeatInterval) {
      // Clear interval if user is not authenticated
      clearInterval(heartbeatInterval);
      setHeartbeatInterval(null);
    }
  }, [currentUser]);

  // Check if user is authenticated on load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Here you would typically fetch user data from your backend API
        // For now, we'll just check localStorage
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        
        if (storedUser && token) {
          setCurrentUser(JSON.parse(storedUser));
          // Send initial heartbeat after login check
          sendHeartbeat();
        }
      } catch (error) {
        console.error('Authentication error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials: { identifier: string; password: string }) => {
    setLoading(true);
    setError(null);
    
    try {
      // Here you would typically make an API call to your backend
      // For demo purposes, we'll simulate a successful login
      
      // Example API call:
      // const response = await fetch('/api/login', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(credentials)
      // });
      
      // Simulate login delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mocked user data
      const userData: User = {
        id: '1',
        username: credentials.identifier,
        email: `${credentials.identifier}@example.com`,
        role: 'user'
      };
      
      // Store user data
      localStorage.setItem('user', JSON.stringify(userData));
      setCurrentUser(userData);
      
      // Send heartbeat after login
      sendHeartbeat();
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (token) {
        // Try multiple endpoints for logout
        const logoutUrls = [
          '/logout-api/',
          '/api/logout-api/',
          '/auth/logout/'
        ];
        
        let logoutSuccess = false;
        for (const url of logoutUrls) {
          try {
            // Call the logout API to update active status
            const response = await fetch(url, {
              method: 'POST',
              headers: {
                'Authorization': `Token ${token}`,
                'Content-Type': 'application/json'
              },
              // Include the token in the body as well
              body: JSON.stringify({ token })
            });
            
            if (response.ok) {
              console.log(`Successfully logged out via ${url}`);
              logoutSuccess = true;
              break;
            } else {
              console.warn(`Logout failed for ${url} with status ${response.status}`);
            }
          } catch (error) {
            console.log(`Logout failed for ${url}:`, error);
            // Continue trying other endpoints
          }
        }
        
        if (!logoutSuccess) {
          console.warn('All logout endpoints failed, proceeding with local logout');
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('is_admin');
      localStorage.removeItem('username');
      
      // Clear heartbeat interval
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
        setHeartbeatInterval(null);
      }
      
      setCurrentUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ currentUser, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider; 